import { ArrowRight } from '@mui/icons-material'
import {
  Autocomplete,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  InputAdornment,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Typography
} from '@mui/material'
import { GreenText } from 'components'
import { useLayout } from 'context'
import * as controllers from 'controllers'
import { CompanyDto, VehicleCheckInDetailsDto } from 'dtos'
import { vehicleCheckInStep1ValidationSchema } from 'dtos/vehicleCheckInDetailsDto'
import { Formik } from 'formik'
import { useLoadingState } from 'hooks'
import { AUTOCOMPLETE_PAGESIZE, DEBOUNCE_DELAY, Junction, KG, kPa, LB, PSI } from 'localConstants'
import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useDebounce } from 'utils'
import VehicleCheckInStepper from './VehicleCheckInStepper'

interface Step1VehicleDetailsProps {
  disabled?: boolean
  initialValues: VehicleCheckInDetailsDto
  onNext: (values: VehicleCheckInDetailsDto) => void
}

export default function Step1VehicleDetails({ disabled, initialValues, onNext }: Step1VehicleDetailsProps) {
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const [loadingState, setLoadingState] = useLoadingState({
    isGettingThirdPartyClusterConversionCompanies: false,
    isGettingThirdPartyAdditionalProgrammingCompanies: false
  })

  const [thirdPartyClusterConversionCompanies, setThirdPartyClusterConversionCompanies] = useState<CompanyDto[]>([])
  const [thirdPartyAdditionalProgrammingCompanies, setThirdPartyAdditionalProgrammingCompanies] = useState<CompanyDto[]>([])

  const getCompaniesAutocompleteBase = (search: string, statuses: string[]) =>
    controllers.getCompanies({
      junction: Junction.AND,
      order: 'asc',
      orderBy: 'name',
      page: 0,
      pageSize: AUTOCOMPLETE_PAGESIZE,
      search,
      statuses
    })

  const getThirdPartyClusterConversionCompaniesAutocomplete = useDebounce((search: string) => {
    setLoadingState('isGettingThirdPartyClusterConversionCompanies', true)
    getCompaniesAutocompleteBase(search, ['isActive', 'isClusterConversionCompany'])
      .then(response => {
        setThirdPartyClusterConversionCompanies(response.value)
      })
      .finally(() => {
        setLoadingState('isGettingThirdPartyClusterConversionCompanies', false)
      })
  }, DEBOUNCE_DELAY)

  const getThirdPartyAdditionalProgrammingCompaniesAutocomplete = useDebounce((search: string) => {
    setLoadingState('isGettingThirdPartyAdditionalProgrammingCompanies', true)
    getCompaniesAutocompleteBase(search, ['isActive', 'isClusterProgrammerCompany'])
      .then(response => {
        setThirdPartyAdditionalProgrammingCompanies(response.value)
      })
      .finally(() => {
        setLoadingState('isGettingThirdPartyAdditionalProgrammingCompanies', false)
      })
  }, DEBOUNCE_DELAY)

  return (
    <Formik
      enableReinitialize={true}
      initialValues={initialValues}
      onSubmit={(values, formikHelpers) => {
        onNext(values)
        formikHelpers.setSubmitting(false)
      }}
      validateOnBlur={false}
      validateOnChange={true}
      validationSchema={vehicleCheckInStep1ValidationSchema}
    >
      {({
        errors,
        handleBlur,
        handleChange,
        handleSubmit,
        isSubmitting,
        setFieldValue,
        setValues,
        touched,
        setTouched,
        values,
        validateForm
      }) => (
        <form onSubmit={handleSubmit}>
          <Card>
            <CardContent>
              <Grid container spacing={1} alignItems='center'>
                <Grid item xs={12}>
                  <VehicleCheckInStepper activeStep={0} />
                </Grid>

                <Grid item xs={12}>
                  <Divider />
                </Grid>

                <Grid item xs={12}>
                  <Typography fontWeight='bold' variant='body1'>
                    Vehicle Details
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    disabled={isLoading}
                    error={Boolean(touched.vehicleCheckInOdometerReadingMileage && errors.vehicleCheckInOdometerReadingMileage)}
                    helperText={touched.vehicleCheckInOdometerReadingMileage && errors.vehicleCheckInOdometerReadingMileage}
                    label='Vehicle Check-In Odometer Reading'
                    type='number'
                    name='vehicleCheckInOdometerReadingMileage'
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.vehicleCheckInOdometerReadingMileage || ''}
                  />
                </Grid>

                <Grid item xs={12}>
                  <RadioGroup
                    name='vehicleCheckInOdometerReadingUnitOfMeasurement'
                    row
                    value={values.vehicleCheckInOdometerReadingUnitOfMeasurement}
                    onChange={event => {
                      setFieldValue('vehicleCheckInOdometerReadingUnitOfMeasurement', event.target.value)

                      if (event.target.value === 'Kilometers') {
                        setFieldValue('convertCluster', true)
                      } else {
                        setFieldValue('convertCluster', false)
                        setFieldValue('vehicleCheckInClusterConversionInHouseOrThirdParty', undefined)
                        setFieldValue('vehicleCheckInThirdPartyClusterConversionCompany', undefined)
                        setFieldValue('vehicleCheckInAdditionalProgramming', undefined)
                        setFieldValue('vehicleCheckInAdditionalProgrammingInHouseOrThirdParty', undefined)
                        setFieldValue('vehicleCheckInThirdPartyAdditionalProgrammingCompany', undefined)
                      }
                    }}
                  >
                    <FormControlLabel
                      value='Miles'
                      control={
                        <Radio
                          checked={values.vehicleCheckInOdometerReadingUnitOfMeasurement === 'Miles'}
                          disabled={isLoading}
                        />
                      }
                      label='Miles'
                    />
                    <FormControlLabel
                      value='Kilometers'
                      control={
                        <Radio
                          checked={values.vehicleCheckInOdometerReadingUnitOfMeasurement === 'Kilometers'}
                          disabled={isLoading}
                        />
                      }
                      label='Kilometers'
                    />
                  </RadioGroup>
                </Grid>

                <Grid item xs={12}>
                  <Typography fontWeight='bold' variant='body1'>
                    Convert Cluster
                  </Typography>
                </Grid>

                <Grid item xs={6} sm={6}>
                  <RadioGroup
                    name='convertCluster'
                    onChange={e => {
                      setFieldValue('convertCluster', e.target.value === 'true')

                      if (e.target.value !== 'true') {
                        setFieldValue('vehicleCheckInClusterConversionInHouseOrThirdParty', undefined)
                        setFieldValue('vehicleCheckInThirdPartyClusterConversionCompany', undefined)
                        setFieldValue('vehicleCheckInAdditionalProgramming', undefined)
                        setFieldValue('vehicleCheckInAdditionalProgrammingInHouseOrThirdParty', undefined)
                        setFieldValue('vehicleCheckInThirdPartyAdditionalProgrammingCompany', undefined)
                      }
                    }}
                    row
                    value={values.convertCluster}
                  >
                    <FormControlLabel
                      value='true'
                      control={<Radio checked={values.convertCluster === true} disabled={isLoading} />}
                      label='Yes'
                    />
                    <FormControlLabel
                      value='false'
                      control={<Radio checked={values.convertCluster === false} disabled={isLoading} />}
                      label='No'
                    />
                  </RadioGroup>
                </Grid>

                <Grid item xs={12}>
                  <Typography fontWeight='bold' variant='body1'>
                    Cluster Conversion In-House or 3rd Party
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <RadioGroup
                    name='vehicleCheckInClusterConversionInHouseOrThirdParty'
                    row
                    value={values.vehicleCheckInClusterConversionInHouseOrThirdParty}
                    onChange={event => {
                      setFieldValue('vehicleCheckInClusterConversionInHouseOrThirdParty', event.target.value)

                      if (event.target.value === 'In-House') {
                        setFieldValue('vehicleCheckInThirdPartyClusterConversionCompany', undefined)
                      }
                    }}
                  >
                    <FormControlLabel
                      value='In-House'
                      control={
                        <Radio
                          checked={values.vehicleCheckInClusterConversionInHouseOrThirdParty === 'In-House'}
                          disabled={isLoading || !values.convertCluster}
                        />
                      }
                      label='In-House'
                    />
                    <FormControlLabel
                      value='3rd Party'
                      control={
                        <Radio
                          checked={values.vehicleCheckInClusterConversionInHouseOrThirdParty === '3rd Party'}
                          disabled={isLoading || !values.convertCluster}
                        />
                      }
                      label='3rd Party'
                    />
                  </RadioGroup>
                </Grid>

                <Grid item xs={12}>
                  <Autocomplete
                    autoHighlight
                    autoSelect
                    disabled={
                      isLoading ||
                      isSubmitting ||
                      disabled ||
                      values.vehicleCheckInClusterConversionInHouseOrThirdParty !== '3rd Party'
                    }
                    disableListWrap
                    getOptionLabel={vehicleCheckInThirdPartyClusterConversionCompany =>
                      vehicleCheckInThirdPartyClusterConversionCompany.name!
                    }
                    noOptionsText={
                      loadingState.isGettingThirdPartyClusterConversionCompanies
                        ? 'Searching Third Party Cluster Conversion Companies...'
                        : 'No Third Party Cluster Conversion Companies. Start typing to search for Third Party Cluster Conversion Companies...'
                    }
                    onBlur={handleBlur}
                    onChange={(_e, value) => {
                      setFieldValue('vehicleCheckInThirdPartyClusterConversionCompany', value || undefined)
                    }}
                    onInputChange={(_e, value, _reason) => {
                      getThirdPartyClusterConversionCompaniesAutocomplete(value)
                    }}
                    options={thirdPartyClusterConversionCompanies.map(vehicleCheckInThirdPartyClusterConversionCompany => ({
                      companyGuid: vehicleCheckInThirdPartyClusterConversionCompany.companyGuid,
                      name: vehicleCheckInThirdPartyClusterConversionCompany.name
                    }))}
                    isOptionEqualToValue={(option, value) => option.companyGuid === value.companyGuid}
                    renderInput={params => (
                      <TextField
                        {...params}
                        error={Boolean(
                          touched.vehicleCheckInThirdPartyClusterConversionCompany &&
                            errors.vehicleCheckInThirdPartyClusterConversionCompany
                        )}
                        helperText={
                          touched.vehicleCheckInThirdPartyClusterConversionCompany &&
                          errors.vehicleCheckInThirdPartyClusterConversionCompany
                        }
                        label='3rd Party Cluster Conversion Company'
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingState.isGettingThirdPartyClusterConversionCompanies && (
                                <InputAdornment position='end'>
                                  <CircularProgress size={24} />
                                </InputAdornment>
                              )}
                              {params.InputProps.endAdornment}
                            </>
                          )
                        }}
                      />
                    )}
                    value={
                      values.vehicleCheckInThirdPartyClusterConversionCompany
                        ? {
                            companyGuid: values.vehicleCheckInThirdPartyClusterConversionCompany.companyGuid,
                            name: values.vehicleCheckInThirdPartyClusterConversionCompany.name
                          }
                        : null
                    }
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography fontWeight='bold' variant='body1'>
                    Additional Programming
                  </Typography>
                </Grid>

                <Grid item xs={6} sm={6}>
                  <RadioGroup
                    name='vehicleCheckInAdditionalProgramming'
                    onChange={e => {
                      setFieldValue('vehicleCheckInAdditionalProgramming', e.target.value === 'true')

                      if (e.target.value !== 'true') {
                        setFieldValue('vehicleCheckInAdditionalProgrammingInHouseOrThirdParty', undefined)
                        setFieldValue('vehicleCheckInThirdPartyAdditionalProgrammingCompany', undefined)
                      }
                    }}
                    row
                    value={values.vehicleCheckInAdditionalProgramming}
                  >
                    <FormControlLabel
                      value='true'
                      control={
                        <Radio
                          checked={values.vehicleCheckInAdditionalProgramming === true}
                          disabled={isLoading || !values.convertCluster}
                        />
                      }
                      label='Yes'
                    />
                    <FormControlLabel
                      value='false'
                      control={
                        <Radio
                          checked={values.vehicleCheckInAdditionalProgramming === false}
                          disabled={isLoading || !values.convertCluster}
                        />
                      }
                      label='No'
                    />
                  </RadioGroup>
                </Grid>

                <Grid item xs={12}>
                  <Typography fontWeight='bold' variant='body1'>
                    Additional Programming In-House or 3rd Party
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <RadioGroup
                    name='vehicleCheckInAdditionalProgrammingInHouseOrThirdParty'
                    row
                    value={values.vehicleCheckInAdditionalProgrammingInHouseOrThirdParty}
                    onChange={event => {
                      setFieldValue('vehicleCheckInAdditionalProgrammingInHouseOrThirdParty', event.target.value)

                      if (event.target.value === 'In-House') {
                        setFieldValue('vehicleCheckInThirdPartyAdditionalProgrammingCompany', undefined)
                      }
                    }}
                  >
                    <FormControlLabel
                      value='In-House'
                      control={
                        <Radio
                          checked={values.vehicleCheckInAdditionalProgrammingInHouseOrThirdParty === 'In-House'}
                          disabled={isLoading || values.vehicleCheckInAdditionalProgramming !== true}
                        />
                      }
                      label='In-House'
                    />
                    <FormControlLabel
                      value='3rd Party'
                      control={
                        <Radio
                          checked={values.vehicleCheckInAdditionalProgrammingInHouseOrThirdParty === '3rd Party'}
                          disabled={isLoading || values.vehicleCheckInAdditionalProgramming !== true}
                        />
                      }
                      label='3rd Party'
                    />
                  </RadioGroup>
                </Grid>

                <Grid item xs={12}>
                  <Autocomplete
                    autoHighlight
                    autoSelect
                    disabled={
                      isLoading ||
                      isSubmitting ||
                      disabled ||
                      values.vehicleCheckInAdditionalProgrammingInHouseOrThirdParty !== '3rd Party'
                    }
                    disableListWrap
                    getOptionLabel={vehicleCheckInThirdPartyAdditionalProgrammingCompany =>
                      vehicleCheckInThirdPartyAdditionalProgrammingCompany.name!
                    }
                    noOptionsText={
                      loadingState.isGettingThirdPartyAdditionalProgrammingCompanies
                        ? 'Searching Third Party Additional Programming Companies...'
                        : 'No Third Party Additional Programming Companies. Start typing to search for Third Party Additional Programming Companies...'
                    }
                    onBlur={handleBlur}
                    onChange={(_e, value) => {
                      setFieldValue('vehicleCheckInThirdPartyAdditionalProgrammingCompany', value || undefined)
                    }}
                    onInputChange={(_e, value, _reason) => {
                      getThirdPartyAdditionalProgrammingCompaniesAutocomplete(value)
                    }}
                    options={thirdPartyAdditionalProgrammingCompanies.map(
                      vehicleCheckInThirdPartyAdditionalProgrammingCompany => ({
                        companyGuid: vehicleCheckInThirdPartyAdditionalProgrammingCompany.companyGuid,
                        name: vehicleCheckInThirdPartyAdditionalProgrammingCompany.name
                      })
                    )}
                    isOptionEqualToValue={(option, value) => option.companyGuid === value.companyGuid}
                    renderInput={params => (
                      <TextField
                        {...params}
                        error={Boolean(
                          touched.vehicleCheckInThirdPartyAdditionalProgrammingCompany &&
                            errors.vehicleCheckInThirdPartyAdditionalProgrammingCompany
                        )}
                        helperText={
                          touched.vehicleCheckInThirdPartyAdditionalProgrammingCompany &&
                          errors.vehicleCheckInThirdPartyAdditionalProgrammingCompany
                        }
                        label='3rd Party Additional Programming Company'
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingState.isGettingThirdPartyAdditionalProgrammingCompanies && (
                                <InputAdornment position='end'>
                                  <CircularProgress size={24} />
                                </InputAdornment>
                              )}
                              {params.InputProps.endAdornment}
                            </>
                          )
                        }}
                      />
                    )}
                    value={
                      values.vehicleCheckInThirdPartyAdditionalProgrammingCompany
                        ? {
                            companyGuid: values.vehicleCheckInThirdPartyAdditionalProgrammingCompany.companyGuid,
                            name: values.vehicleCheckInThirdPartyAdditionalProgrammingCompany.name
                          }
                        : null
                    }
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    disabled={isLoading}
                    error={Boolean(touched.vehicleCheckInMFGBuildDateMonth && errors.vehicleCheckInMFGBuildDateMonth)}
                    helperText={touched.vehicleCheckInMFGBuildDateMonth && errors.vehicleCheckInMFGBuildDateMonth}
                    label='MFG Build Date (Month)'
                    name='vehicleCheckInMFGBuildDateMonth'
                    onBlur={handleBlur}
                    onChange={handleChange}
                    type='number'
                    value={values.vehicleCheckInMFGBuildDateMonth || ''}
                  >
                    <MenuItem value={1}>1</MenuItem>
                    <MenuItem value={2}>2</MenuItem>
                    <MenuItem value={3}>3</MenuItem>
                    <MenuItem value={4}>4</MenuItem>
                    <MenuItem value={5}>5</MenuItem>
                    <MenuItem value={6}>6</MenuItem>
                    <MenuItem value={7}>7</MenuItem>
                    <MenuItem value={8}>8</MenuItem>
                    <MenuItem value={9}>9</MenuItem>
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={11}>11</MenuItem>
                    <MenuItem value={12}>12</MenuItem>
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    disabled={isLoading}
                    error={Boolean(touched.vehicleCheckInMFGBuildDateYear && errors.vehicleCheckInMFGBuildDateYear)}
                    helperText={touched.vehicleCheckInMFGBuildDateYear && errors.vehicleCheckInMFGBuildDateYear}
                    label='MFG Build Date (Year)'
                    name='vehicleCheckInMFGBuildDateYear'
                    onBlur={handleBlur}
                    onChange={handleChange}
                    type='number'
                    value={values.vehicleCheckInMFGBuildDateYear || ''}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    disabled={isLoading}
                    error={Boolean(touched.vehicleCheckInParkingSpace && errors.vehicleCheckInParkingSpace)}
                    helperText={touched.vehicleCheckInParkingSpace && errors.vehicleCheckInParkingSpace}
                    label='Parking Space'
                    name='vehicleCheckInParkingSpace'
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.vehicleCheckInParkingSpace || ''}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography fontWeight='bold' variant='body1'>
                    Weight
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    disabled={isLoading}
                    error={Boolean(touched.vehicleCheckInWeightGVWR && errors.vehicleCheckInWeightGVWR)}
                    helperText={touched.vehicleCheckInWeightGVWR && errors.vehicleCheckInWeightGVWR}
                    label='GVWR'
                    name='vehicleCheckInWeightGVWR'
                    onBlur={handleBlur}
                    onChange={handleChange}
                    type='number'
                    value={values.vehicleCheckInWeightGVWR || ''}
                  />
                </Grid>

                <Grid item xs={6} sm={6}>
                  <RadioGroup
                    name='vehicleCheckInWeightGVWRUnitOfMeasurement'
                    onChange={handleChange}
                    row
                    value={values.vehicleCheckInWeightGVWRUnitOfMeasurement}
                  >
                    <FormControlLabel
                      control={<Radio checked={values.vehicleCheckInWeightGVWRUnitOfMeasurement === KG} disabled={isLoading} />}
                      label='Kg'
                      value={KG}
                    />

                    <FormControlLabel
                      control={<Radio checked={values.vehicleCheckInWeightGVWRUnitOfMeasurement === LB} disabled={isLoading} />}
                      label='Lb'
                      value={LB}
                    />
                  </RadioGroup>
                </Grid>

                <Grid item xs={12} />

                <Grid item xs={12} sm={6}>
                  <TextField
                    disabled={isLoading}
                    error={Boolean(touched.vehicleCheckInWeightGAWRFR && errors.vehicleCheckInWeightGAWRFR)}
                    helperText={touched.vehicleCheckInWeightGAWRFR && errors.vehicleCheckInWeightGAWRFR}
                    label='GAWR FR'
                    name='vehicleCheckInWeightGAWRFR'
                    onBlur={handleBlur}
                    onChange={handleChange}
                    type='number'
                    value={values.vehicleCheckInWeightGAWRFR || ''}
                  />
                </Grid>

                <Grid item xs={6} sm={6}>
                  <RadioGroup
                    name='vehicleCheckInWeightGAWRFRUnitOfMeasurement'
                    onChange={handleChange}
                    row
                    value={values.vehicleCheckInWeightGAWRFRUnitOfMeasurement}
                  >
                    <FormControlLabel
                      control={<Radio checked={values.vehicleCheckInWeightGAWRFRUnitOfMeasurement === KG} disabled={isLoading} />}
                      label='Kg'
                      value={KG}
                    />

                    <FormControlLabel
                      control={<Radio checked={values.vehicleCheckInWeightGAWRFRUnitOfMeasurement === LB} disabled={isLoading} />}
                      label='Lb'
                      value={LB}
                    />
                  </RadioGroup>
                </Grid>

                <Grid item xs={12} />

                <Grid item xs={12} sm={6}>
                  <TextField
                    disabled={isLoading}
                    error={Boolean(touched.vehicleCheckInWeightGAWRRR && errors.vehicleCheckInWeightGAWRRR)}
                    helperText={touched.vehicleCheckInWeightGAWRRR && errors.vehicleCheckInWeightGAWRRR}
                    label='GAWR RR'
                    name='vehicleCheckInWeightGAWRRR'
                    onBlur={handleBlur}
                    onChange={handleChange}
                    type='number'
                    value={values.vehicleCheckInWeightGAWRRR || ''}
                  />
                </Grid>

                <Grid item xs={6} sm={6}>
                  <RadioGroup
                    name='vehicleCheckInWeightGAWRRRUnitOfMeasurement'
                    onChange={handleChange}
                    row
                    value={values.vehicleCheckInWeightGAWRRRUnitOfMeasurement}
                  >
                    <FormControlLabel
                      control={<Radio checked={values.vehicleCheckInWeightGAWRRRUnitOfMeasurement === KG} disabled={isLoading} />}
                      label='Kg'
                      value={KG}
                    />

                    <FormControlLabel
                      control={<Radio checked={values.vehicleCheckInWeightGAWRRRUnitOfMeasurement === LB} disabled={isLoading} />}
                      label='Lb'
                      value={LB}
                    />
                  </RadioGroup>
                </Grid>

                <Grid item xs={12}>
                  <Typography fontWeight='bold' variant='body1'>
                    Tires
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    disabled={isLoading}
                    error={Boolean(touched.vehicleCheckInFrontTires && errors.vehicleCheckInFrontTires)}
                    helperText={touched.vehicleCheckInFrontTires && errors.vehicleCheckInFrontTires}
                    label='Front Tires'
                    name='vehicleCheckInFrontTires'
                    onBlur={handleBlur}
                    onChange={handleChange}
                    type='string'
                    value={values.vehicleCheckInFrontTires || ''}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    disabled={isLoading}
                    error={Boolean(touched.vehicleCheckInFrontRims && errors.vehicleCheckInFrontRims)}
                    helperText={touched.vehicleCheckInFrontRims && errors.vehicleCheckInFrontRims}
                    label='Front Rims'
                    name='vehicleCheckInFrontRims'
                    onBlur={handleBlur}
                    onChange={handleChange}
                    type='string'
                    value={values.vehicleCheckInFrontRims || ''}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    disabled={isLoading}
                    error={Boolean(touched.vehicleCheckInFrontTiresPressure && errors.vehicleCheckInFrontTiresPressure)}
                    helperText={touched.vehicleCheckInFrontTiresPressure && errors.vehicleCheckInFrontTiresPressure}
                    label='Front Tires PSI'
                    name='vehicleCheckInFrontTiresPressure'
                    onBlur={handleBlur}
                    onChange={handleChange}
                    type='number'
                    value={values.vehicleCheckInFrontTiresPressure || ''}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <RadioGroup
                    name='vehicleCheckInFrontTiresPressureUnitOfMeasurement'
                    onChange={handleChange}
                    row
                    value={values.vehicleCheckInFrontTiresPressureUnitOfMeasurement}
                  >
                    <FormControlLabel
                      control={
                        <Radio
                          checked={values.vehicleCheckInFrontTiresPressureUnitOfMeasurement === kPa}
                          disabled={isLoading}
                        />
                      }
                      label='kPa'
                      value={kPa}
                    />
                    <FormControlLabel
                      control={
                        <Radio
                          checked={values.vehicleCheckInFrontTiresPressureUnitOfMeasurement === PSI}
                          disabled={isLoading}
                        />
                      }
                      label='PSI'
                      value={PSI}
                    />
                  </RadioGroup>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    disabled={isLoading}
                    error={Boolean(touched.vehicleCheckInRearTires && errors.vehicleCheckInRearTires)}
                    helperText={touched.vehicleCheckInRearTires && errors.vehicleCheckInRearTires}
                    label='Rear Tires'
                    name='vehicleCheckInRearTires'
                    onBlur={handleBlur}
                    onChange={handleChange}
                    type='string'
                    value={values.vehicleCheckInRearTires || ''}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    disabled={isLoading}
                    error={Boolean(touched.vehicleCheckInRearRims && errors.vehicleCheckInRearRims)}
                    helperText={touched.vehicleCheckInRearRims && errors.vehicleCheckInRearRims}
                    label='Rear Rims'
                    name='vehicleCheckInRearRims'
                    onBlur={handleBlur}
                    onChange={handleChange}
                    type='string'
                    value={values.vehicleCheckInRearRims || ''}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    disabled={isLoading}
                    error={Boolean(touched.vehicleCheckInRearTiresPressure && errors.vehicleCheckInRearTiresPressure)}
                    helperText={touched.vehicleCheckInRearTiresPressure && errors.vehicleCheckInRearTiresPressure}
                    label='Rear Tires PSI'
                    name='vehicleCheckInRearTiresPressure'
                    onBlur={handleBlur}
                    onChange={handleChange}
                    type='number'
                    value={values.vehicleCheckInRearTiresPressure || ''}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <RadioGroup
                    name='vehicleCheckInRearTiresPressureUnitOfMeasurement'
                    onChange={handleChange}
                    row
                    value={values.vehicleCheckInRearTiresPressureUnitOfMeasurement}
                  >
                    <FormControlLabel
                      control={
                        <Radio checked={values.vehicleCheckInRearTiresPressureUnitOfMeasurement === kPa} disabled={isLoading} />
                      }
                      label='kPa'
                      value={kPa}
                    />
                    <FormControlLabel
                      control={
                        <Radio checked={values.vehicleCheckInRearTiresPressureUnitOfMeasurement === PSI} disabled={isLoading} />
                      }
                      label='PSI'
                      value={PSI}
                    />
                  </RadioGroup>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    disabled={isLoading}
                    error={Boolean(touched.vehicleCheckInNumberOfRearTires && errors.vehicleCheckInNumberOfRearTires)}
                    helperText={touched.vehicleCheckInNumberOfRearTires && errors.vehicleCheckInNumberOfRearTires}
                    label='Number Of Rear Tires'
                    name='vehicleCheckInNumberOfRearTires'
                    onBlur={handleBlur}
                    onChange={handleChange}
                    type='number'
                    select
                    value={values.vehicleCheckInNumberOfRearTires || ''}
                  >
                    <MenuItem value={2}>2</MenuItem>
                    <MenuItem value={4}>4</MenuItem>
                  </TextField>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    disabled={isLoading}
                    error={Boolean(touched.importerNotes && errors.importerNotes)}
                    helperText={touched.importerNotes && errors.importerNotes}
                    label='Importer Notes'
                    name='importerNotes'
                    maxRows={5}
                    minRows={5}
                    multiline
                    onBlur={handleBlur}
                    onChange={handleChange}
                    type='string'
                    value={values.importerNotes}
                  />
                </Grid>

                <Grid item xs={12}>
                  <GreenText variant='outlined'>
                    {`**Vehicle Check-In Steps:**

Displays the current step of the vehicle in the check-in process.

**Vehicle Check-In Odometer Reading:**

Prepopulated with the Exporter Mileage from the Vehicle Details – Exporter tab.

A required free-text field for numeric input only.

**Miles/Kilometers Radio Buttons:**

Default selection is based on the Exporter Mileage units from the Vehicle Details – Exporter tab.

**Convert Cluster Radio Buttons:**

Defaults to Yes if the Exporter mileage is in kilometers.

Defaults to No if the Exporter mileage is in miles.

**Cluster Conversion In-House or 3rd Party Radio Buttons:**

Enabled when Convert Cluster = Yes.

**3rd Party Cluster Conversion Company Drop-Down:**

A drop-down field that displays all active cluster conversion companies within the system.

Enabled when Cluster Conversion In-House or 3rd Party = 3rd Party.

**Additional Programming Radio Buttons:**

Radio buttons used to select "Yes" or "No" for whether additional programming is needed for the vehicle.

Enabled when Convert Cluster = Yes.

**Additional Programming In-House or 3rd Party Radio Buttons:**

Radio buttons used to select whether the additional programming will be done in-house or by a third party.

Enabled when Additional Programming = Yes.

**3rd Party Additional Programming Company Drop-Down:**

A drop-down field that displays all active cluster programming companies within the system.

Enabled when Additional Programming In-House or 3rd Party = 3rd Party.

**MFG Build Date (Month) Drop-Down:**

A drop-down field displaying the months as numbers (1–12) for selecting the vehicle's build month.

The field is prepopulated with the MFG Build Date (Month) value from the Add/Edit VIN dialog.

**MFG Build Date (Year):**

A required free-text numeric field for entering the vehicle's build year.

The field is prepopulated with the MFG Build Date (Year) value from the Add/Edit VIN dialog.

**Parking Space:**

A required free-text field with a 20-character limit.

**GVWR:**

A required free-text field for entering the vehicle's Gross Vehicle Weight Rating (GVWR).

Prepopulated with the corresponding Weight values from the Add/Edit VIN dialog.


**Kg/Lb Radio Buttons:**

Radio buttons used to select whether GVWR is measured in kilograms (Kg) or pounds (Lb).

Prepopulated with the corresponding Weight values from the Add/Edit VIN dialog.

**GAWR FR:**

A required free-text field for entering the Gross Axle Weight Rating (Front).

Prepopulated with the corresponding Weight values from the Add/Edit VIN dialog.

**GAWR FR Kg/Lb Radio Buttons:**

Radio buttons used to select whether GAWR FR is measured in kilograms (Kg) or pounds (Lb).

Prepopulated with the corresponding Weight values from the Add/Edit VIN dialog.

**GAWR RR:**

A required free-text field for entering the Gross Axle Weight Rating (Rear).

Prepopulated with the corresponding Weight values from the Add/Edit VIN dialog.

**GAWR RR Kg/Lb Radio Buttons:**

Radio buttons used to select whether GAWR RR is measured in kilograms (Kg) or pounds (Lb).

Prepopulated with the corresponding Weight values from the Add/Edit VIN dialog.

**Front Tires:**

A required free-text field for entering the front tire details.

Prepopulated with the corresponding Tires values from the Add/Edit VIN dialog.

**Front Rims:**

A required free-text field for entering the front rim details.

Prepopulated with the corresponding Tires values from the Add/Edit VIN dialog.

**Front Tires PSI:**

A required free-text field for entering the front tire pressure.

Prepopulated with the corresponding Tires values from the Add/Edit VIN dialog.

**kPa/PSI Radio Buttons:**

Radio buttons used to select whether the tire pressure is measured in kilopascals (kPa) or pounds per square inch (PSI).

Prepopulated with the corresponding Tires values from the Add/Edit VIN dialog.

**Rear Tires:**

A required free-text field for entering the rear tire details.

Prepopulated with the corresponding Tires values from the Add/Edit VIN dialog.

**Rear Rims:**

A required free-text field for entering the rear rim details.

Prepopulated with the corresponding Tires values from the Add/Edit VIN dialog.

**Rear Tires PSI:**

A required free-text field for entering the rear tire pressure.

**kPa/PSI Radio Buttons:**

Radio buttons used to select whether the rear tire pressure is measured in kilopascals (kPa) or pounds per square inch (PSI).

Prepopulated with the corresponding Tires values from the Add/Edit VIN dialog.

**Number of Rear Tires Drop-Down:**

A drop-down field displaying options (2 or 4) for selecting the number of rear tires the vehicle has.

Prepopulated with the corresponding Tires values from the Add/Edit VIN dialog.

**Notes:**

A free-text field for additional notes or comments.

**NEXT Button:**

Clicking the NEXT button advances the user to the next step in the vehicle check-in process.

Pressing the Next button saves the entered information for the vehicle check-in process, ensuring that if the user stops and returns later, the data will be retained.`}
                  </GreenText>
                </Grid>

                <Grid item xs={12}>
                  <Button
                    color='primary'
                    disabled={
                      !Boolean(values.vehicleCheckInOdometerReadingMileage) ||
                      !Boolean(values.vehicleCheckInOdometerReadingUnitOfMeasurement) ||
                      values.convertCluster === undefined ||
                      !Boolean(values.vehicleCheckInMFGBuildDateMonth) ||
                      !Boolean(values.vehicleCheckInMFGBuildDateYear) ||
                      !Boolean(values.vehicleCheckInWeightGVWR) ||
                      !Boolean(values.vehicleCheckInWeightGVWRUnitOfMeasurement) ||
                      !Boolean(values.vehicleCheckInWeightGAWRFR) ||
                      !Boolean(values.vehicleCheckInWeightGAWRFRUnitOfMeasurement) ||
                      !Boolean(values.vehicleCheckInWeightGAWRRR) ||
                      !Boolean(values.vehicleCheckInWeightGAWRRRUnitOfMeasurement) ||
                      !Boolean(values.vehicleCheckInFrontTires) ||
                      !Boolean(values.vehicleCheckInFrontRims) ||
                      !Boolean(values.vehicleCheckInFrontTiresPressure) ||
                      !Boolean(values.vehicleCheckInFrontTiresPressureUnitOfMeasurement) ||
                      !Boolean(values.vehicleCheckInRearTires) ||
                      !Boolean(values.vehicleCheckInRearRims) ||
                      !Boolean(values.vehicleCheckInRearTiresPressure) ||
                      !Boolean(values.vehicleCheckInRearTiresPressureUnitOfMeasurement) ||
                      !Boolean(values.vehicleCheckInNumberOfRearTires)
                    }
                    endIcon={<ArrowRight />}
                    fullWidth
                    onClick={() => {
                      console.log(errors)
                    }}
                    type='submit'
                    variant='contained'
                  >
                    NEXT
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </form>
      )}
    </Formik>
  )
}
